import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { chunk, isEmpty } from 'lodash';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import DiseaseApi from 'api/DiseaseApi';
import LogApi from 'api/LogApi';
import { DiseaseLocationMatrixModel, LocationRiskSummaryRequestModel } from 'client/dto';

import diseaseActivitiesRelevantSelectors from './diseaseActivitiesRelevant.selectors';
import {
  loadDiseaseActivitiesRelevantFinish,
  loadDiseaseActivitiesRelevantStart,
  loadDiseaseActivitiesRelevantSuccess,
  sortDiseaseActivitiesRelevant,
  sortDiseaseActivitiesRelevantDiseasesForLocation,
  sortDiseaseActivitiesRelevantLocationsForDisease,
} from './diseaseActivitiesRelevant.slice';

const NumOfParallelRequests = 3;
const NumOfDiseasesPerRequest = 65;

type FetchDiseaseActivitiesRelevantPayload = {
  locationIds: number[];
  diseaseIds: number[];
  startDate: string;
};

export const fetchRelevantDiseaseActivities = createAction<FetchDiseaseActivitiesRelevantPayload>(
  'diseaseActivitiesRelevant/fetch'
);
export const applySortRelevantDiseaseActivities = createAction(
  'diseaseActivitiesRelevant/applySort'
);

export function* fetchRelevantDiseaseActivitiesSaga(
  action: PayloadAction<FetchDiseaseActivitiesRelevantPayload>
) {
  try {
    const diseaseIds = action.payload.diseaseIds;
    const locationIds = action.payload.locationIds;
    const startDate = action.payload.startDate;

    if (!isEmpty(diseaseIds) && !isEmpty(locationIds) && !isEmpty(startDate)) {
      yield put(loadDiseaseActivitiesRelevantStart());

      const diseaseIdChunks: number[][][] = chunk(
        chunk(diseaseIds, NumOfDiseasesPerRequest),
        NumOfParallelRequests
      );

      for (const diseaseIdChunk of diseaseIdChunks) {
        const diseaseCallEffects = diseaseIdChunk.map(chunkDiseaseIds => {
          const locationRiskSummaryRequestModel: LocationRiskSummaryRequestModel = {
            activityStartDate: startDate,
            activityEndDate: new Date().toISOString(),
            locationIds,
            diseaseIds: chunkDiseaseIds,
          };
          return call(DiseaseApi.getLocationRiskSummary, locationRiskSummaryRequestModel);
        });

        const responses: Record<number, DiseaseLocationMatrixModel>[] = yield all(
          diseaseCallEffects
        );

        yield put(loadDiseaseActivitiesRelevantSuccess(responses));
      }
      yield put(loadDiseaseActivitiesRelevantFinish());
      yield put(applySortRelevantDiseaseActivities());
    }
  } catch (e) {
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.log(e);
  }
}

export function* applySortRelevantDiseaseActivitiesSaga() {
  const sortBy = yield select(diseaseActivitiesRelevantSelectors.getSortBy);
  const diseaseIdsSortBy = yield select(diseaseActivitiesRelevantSelectors.getDiseaseColumnsSortBy);
  const locationIdsSortBy = yield select(diseaseActivitiesRelevantSelectors.getLocationRowsSortBy);

  if (sortBy) {
    yield put(sortDiseaseActivitiesRelevant(sortBy));
  } else if (diseaseIdsSortBy) {
    yield put(sortDiseaseActivitiesRelevantDiseasesForLocation(diseaseIdsSortBy));
  } else if (locationIdsSortBy) {
    yield put(sortDiseaseActivitiesRelevantLocationsForDisease(locationIdsSortBy));
  }
}

function* diseaseActivitiesRelevantSaga() {
  yield takeLatest(fetchRelevantDiseaseActivities, fetchRelevantDiseaseActivitiesSaga);
  yield takeLatest(applySortRelevantDiseaseActivities, applySortRelevantDiseaseActivitiesSaga);
}

export default diseaseActivitiesRelevantSaga;
